import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';

const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const queryClient = useQueryClient();

  // Récupérer les notifications de l'utilisateur
  const { data: notificationsData, isLoading, refetch } = useQuery(
    'notifications',
    () => api.get('/notifications').then(res => res.data.data),
    {
      refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
      onSuccess: (data) => {
        setNotifications(data || []);
        updateUnreadCount(data || []);
      }
    }
  );

  // Récupérer le nombre de notifications non lues
  const { data: unreadData } = useQuery(
    'notifications-unread',
    () => api.get('/notifications/unread-count').then(res => res.data.data),
    {
      refetchInterval: 10000, // Rafraîchir toutes les 10 secondes
      onSuccess: (data) => {
        setUnreadCount(data?.unread_count || 0);
      }
    }
  );

  // Marquer une notification comme lue
  const markAsReadMutation = useMutation(
    ({ id, isGlobal }) => api.put(`/notifications/${id}/mark-read${isGlobal ? '?global=1' : ''}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('notifications-unread');
      },
      onError: (error) => {
        toast.error('Erreur lors de la mise à jour de la notification');
        console.error('Erreur mark as read:', error);
      }
    }
  );

  // Marquer toutes les notifications comme lues
  const markAllAsReadMutation = useMutation(
    () => api.put('/notifications/mark-all-read'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('notifications-unread');
        toast.success('Toutes les notifications ont été marquées comme lues');
      },
      onError: (error) => {
        toast.error('Erreur lors de la mise à jour des notifications');
        console.error('Erreur mark all as read:', error);
      }
    }
  );

  // Supprimer une notification
  const deleteMutation = useMutation(
    ({ id, isGlobal }) => api.delete(`/notifications/${id}${isGlobal ? '?global=1' : ''}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('notifications-unread');
        toast.success('Notification supprimée');
      },
      onError: (error) => {
        toast.error('Erreur lors de la suppression de la notification');
        console.error('Erreur delete notification:', error);
      }
    }
  );

  // Fonction pour mettre à jour le compteur de notifications non lues
  const updateUnreadCount = useCallback((notificationsList) => {
    const unread = notificationsList.filter(n => !n.is_read).length;
    setUnreadCount(unread);
  }, []);

  // Fonction pour obtenir les notifications par type
  const getNotificationsByType = useCallback((type) => {
    return notifications.filter(n => n.type === type);
  }, [notifications]);

  // Fonction pour obtenir les notifications non lues
  const getUnreadNotifications = useCallback(() => {
    return notifications.filter(n => !n.is_read);
  }, [notifications]);

  // Fonction pour obtenir les notifications récentes (dernières 24h)
  const getRecentNotifications = useCallback(() => {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    return notifications.filter(n => {
      const notificationDate = new Date(n.created_at);
      return notificationDate >= oneDayAgo;
    });
  }, [notifications]);

  // Fonction pour rafraîchir manuellement les notifications
  const refreshNotifications = useCallback(() => {
    refetch();
  }, [refetch]);

  // Effet pour mettre à jour les données quand elles changent
  useEffect(() => {
    if (notificationsData) {
      setNotifications(notificationsData);
      updateUnreadCount(notificationsData);
    }
  }, [notificationsData, updateUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteMutation.mutate,
    refreshNotifications,
    getNotificationsByType,
    getUnreadNotifications,
    getRecentNotifications,
    isMarkingAsRead: markAsReadMutation.isLoading,
    isMarkingAllAsRead: markAllAsReadMutation.isLoading,
    isDeleting: deleteMutation.isLoading
  };
};

export default useNotifications;
